/**
 * What the plate costs.
 *
 * ---------------------------------------------------------------------
 * THE COVERAGE LINE COMES BEFORE THE NUMBERS
 *
 * The first thing this page says is how much of the menu it is actually
 * talking about — "19 of 25 costed" — and it says it above the food cost
 * figures rather than under them.
 *
 * That ordering is the whole design. A food cost report that quietly
 * covers three quarters of the menu is not a food cost report, it is a
 * food cost report about three quarters of the menu, and the difference
 * matters most to exactly the person least likely to go looking for it.
 *
 * ---------------------------------------------------------------------
 * AN ITEM WITH NO RECIPE IS A BLANK, NEVER A ZERO
 *
 * Zero cost reads as a hundred per cent margin, which is the single most
 * expensive wrong answer this screen could give — and it would give it
 * about precisely the items nobody has got round to costing. So the cost
 * column is a dash and the row sits at the top of the list under "not
 * costed yet", where it is a task rather than a triumph.
 *
 * ---------------------------------------------------------------------
 * THE VARIANCE IS THE POINT OF THE COST OF SALES SECTION
 *
 * Theoretical usage on its own is a number nobody can act on. Beside the
 * count, it becomes "the kitchen used eight per cent more ribeye than
 * the recipes account for", which is a conversation with a chef. So the
 * run detail loads its variance with it rather than behind another
 * click.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { date as fmtDate, money, titleCase } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface ItemCost {
  id: string;
  code: string;
  name: string;
  has_recipe: boolean;
  understated: boolean;
  recipe_id: string | null;
  cost_minor: number | null;
  recipe_lines: number | null;
  lines_not_costed: number | null;
  lines_with_no_price: number | null;
  price_minor: number | null;
  cost_percent: number | null;
  margin_minor: number | null;
  is_alcohol: boolean;
}

interface Run {
  id: string;
  covers_from: string;
  covers_to: string;
  days: number;
  status: string;
  total_minor: number;
  checks_counted: number;
  lines_costed: number;
  lines_uncosted: number;
  items_uncosted: number;
  understated: boolean;
  entry_number: number | null;
  reversed_reason: string | null;
}

interface Blocker {
  reason: string;
  detail: string | null;
  affected: number;
}

const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

function blockerText(b: Blocker): string {
  switch (b.reason) {
    case 'open_tabs':
      return `${b.affected} check${b.affected === 1 ? '' : 's'} in that span ${b.affected === 1 ? 'is' : 'are'} still open — ${b.detail}. Costing round them would put the missing food into next month's variance.`;
    case 'already_depleted':
      return `${b.detail}. A night costed twice takes the same tenderloin off the shelf twice.`;
    case 'no_storeroom':
      return 'This club has no storeroom, so there is nowhere to take the food out of.';
    default:
      return `${b.reason}${b.detail ? ` — ${b.detail}` : ''}`;
  }
}

/** Today and two weeks back, as the date inputs want them. */
function isoDay(offset = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export function RecipesPage() {
  const { currency, atLeast } = useSession();
  const [openRecipe, setOpenRecipe] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [from, setFrom] = useState(() => isoDay(-14));
  const [to, setTo] = useState(() => isoDay(-1));
  const [refused, setRefused] = useState<Blocker[] | null>(null);

  const mayRun = atLeast('manager');

  const menu = useApi<{ items: ItemCost[]; coverage: any }>('/v1/recipes/menu-cost');
  const runs = useApi<{ runs: Run[] }>('/v1/recipes/depletions');

  const deplete = useMutation(async () => {
    const r = await api<{ computed: boolean; run?: Run; blockers?: Blocker[] }>(
      '/v1/recipes/depletions',
      { method: 'POST', body: { from, to } },
    );
    if (r.computed && r.run) {
      setRefused(null);
      setOpenRun(r.run.id);
    } else {
      setRefused(r.blockers ?? []);
    }
    runs.reload();
    return r;
  });

  if (menu.loading && !menu.data) return <Loading />;

  const items = menu.data?.items ?? [];
  const cov = menu.data?.coverage ?? { costed: 0, total: 0, understated: 0 };
  const uncosted = items.filter((i) => !i.has_recipe);
  const costed = items.filter((i) => i.has_recipe);
  const all = runs.data?.runs ?? [];
  const posted = all.filter((r) => r.status === 'posted');

  const avgCost = costed.filter((i) => i.cost_percent !== null);
  const mean = avgCost.length
    ? avgCost.reduce((n, i) => n + Number(i.cost_percent), 0) / avgCost.length
    : null;

  return (
    <>
      <h1>Recipes &amp; Food Cost</h1>
      <ErrorBanner error={menu.error ?? runs.error ?? deplete.error} />

      {/* Above the numbers, not under them. */}
      <div className={cov.costed < cov.total ? 'notice notice--warn' : 'notice'}>
        <strong>
          {cov.costed} of {cov.total} items on the menu have a recipe.
        </strong>
        {cov.costed < cov.total && (
          <p className="muted small">
            Everything below is about those {cov.costed}. The other {cov.total - cov.costed} are
            sold and not costed, so the club&rsquo;s food cost is understated by whatever they
            come to — which is not a small number if one of them is the burger.
          </p>
        )}
        {cov.understated > 0 && (
          <p className="muted small">
            {cov.understated} more {cov.understated === 1 ? 'has a recipe naming an ingredient' : 'have recipes naming ingredients'} the
            club has never bought, so {cov.understated === 1 ? 'its' : 'their'} cost is short too.
          </p>
        )}
      </div>

      <div className="tiles">
        <Tile
          label="Average food cost"
          value={mean === null ? '—' : `${mean.toFixed(1)}%`}
          note={`across ${avgCost.length} costed item${avgCost.length === 1 ? '' : 's'}`}
        />
        <Tile
          label="Cost of sales posted"
          value={money(posted.reduce((n, r) => n + Number(r.total_minor), 0), currency)}
          note={`${posted.length} run${posted.length === 1 ? '' : 's'}`}
        />
        <Tile label="Not costed yet" value={String(uncosted.length)} />
      </div>

      {/* ============================================================ */}
      {uncosted.length > 0 && (
        <Card title="Not Costed Yet">
          <p className="muted small">
            These sell and the club does not know what they cost. Nothing here is priced at zero —
            a blank is the honest answer and a zero would say the margin is a hundred per cent.
          </p>
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Sells for</th>
                  <th className="num">Costs</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {uncosted.map((i) => (
                  <tr key={i.id}>
                    <td data-label="Item" className="cell--title">{i.name}</td>
                    <td data-label="Sells for" className="num">
                      {i.price_minor === null ? (
                        <span className="muted">no price</span>
                      ) : (
                        money(i.price_minor, currency)
                      )}
                    </td>
                    <td data-label="Costs" className="num muted">—</td>
                    <td className="cell--aside muted small">no recipe</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ============================================================ */}
      <Card title="What Each Plate Costs">
        {costed.length === 0 ? (
          <Empty>No recipes yet. Nothing on the menu has a cost.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Cost</th>
                  <th className="num">Sells for</th>
                  <th className="num">Food cost</th>
                  <th className="num">Margin</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {costed.map((i) => (
                  <tr key={i.id}>
                    <td data-label="Item" className="cell--title">
                      {i.name}
                      {i.understated && (
                        <span className="muted small"> · {i.lines_with_no_price} unpriced</span>
                      )}
                    </td>
                    <td data-label="Cost" className="num">
                      {money(Math.round(Number(i.cost_minor)), currency)}
                    </td>
                    <td data-label="Sells for" className="num">
                      {i.price_minor === null ? (
                        <span className="muted">no price</span>
                      ) : (
                        money(i.price_minor, currency)
                      )}
                    </td>
                    <td data-label="Food cost" className="num">
                      {i.cost_percent === null ? (
                        <span className="muted">—</span>
                      ) : (
                        <Status tone={Number(i.cost_percent) > 35 ? 'warning' : 'good'}>
                          {nf.format(Number(i.cost_percent))}%
                        </Status>
                      )}
                    </td>
                    <td data-label="Margin" className="num">
                      {i.margin_minor === null ? (
                        <span className="muted">—</span>
                      ) : (
                        money(i.margin_minor, currency)
                      )}
                    </td>
                    <td className="cell--action">
                      {i.recipe_id && (
                        <button
                          className="linkish"
                          onClick={() => setOpenRecipe(openRecipe === i.recipe_id ? null : i.recipe_id)}
                        >
                          {openRecipe === i.recipe_id ? 'Close' : 'Recipe'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openRecipe && <RecipeDetail recipeId={openRecipe} />}

      {/* ============================================================ */}
      <Card title="Cost of Sales">
        <p className="muted small">
          What a span of service should have taken off the shelf, according to the recipes. Comps
          count and voids do not — the plate was cooked either way, and only one of the two was
          ever charged for.
        </p>

        {mayRun && (
          <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>From</span>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                     style={{ maxWidth: 170 }} />
            </label>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>To</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                     style={{ maxWidth: 170 }} />
            </label>
            <button disabled={deplete.busy || to < from} onClick={() => deplete.run()}>
              {deplete.busy ? 'Working it out…' : 'Cost this span'}
            </button>
          </div>
        )}

        {refused && refused.length > 0 && (
          <div className="notice notice--warn">
            <strong>That span cannot be costed yet.</strong>
            <ul>
              {refused.map((b) => (
                <li key={b.reason}>{blockerText(b)}</li>
              ))}
            </ul>
          </div>
        )}

        {all.length === 0 ? (
          <Empty>No service has been costed yet, so cost of food sold is empty.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Covering</th>
                  <th className="num">Checks</th>
                  <th className="num">Cost</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {all.map((r) => (
                  <tr key={r.id} className={r.status === 'reversed' ? 'muted' : undefined}>
                    <td data-label="Covering" className="cell--title">
                      {fmtDate(r.covers_from)} – {fmtDate(r.covers_to)}
                    </td>
                    <td data-label="Checks" className="num">{r.checks_counted}</td>
                    <td data-label="Cost" className="num">
                      {money(r.total_minor, currency)}
                    </td>
                    <td data-label="State">
                      <Status
                        tone={
                          r.status === 'posted' ? 'good'
                            : r.status === 'reversed' ? 'neutral' : 'warning'
                        }
                      >
                        {titleCase(r.status)}
                      </Status>
                      {r.entry_number && (
                        <span className="muted small"> · entry {r.entry_number}</span>
                      )}
                    </td>
                    <td className="cell--action">
                      <button
                        className="linkish"
                        onClick={() => setOpenRun(openRun === r.id ? null : r.id)}
                      >
                        {openRun === r.id ? 'Close' : 'Open'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openRun && (
        <RunDetail
          runId={openRun}
          mayPost={atLeast('controller')}
          onChanged={() => runs.reload()}
        />
      )}
    </>
  );
}

// =====================================================================
// One recipe
// =====================================================================

function RecipeDetail({ recipeId }: { recipeId: string }) {
  const { currency } = useSession();
  const { data, error, loading } = useApi<{ recipe: any; lines: any[] }>(
    `/v1/recipes/${recipeId}`,
    [recipeId],
  );

  if (loading && !data) return <Loading rows={2} />;
  if (!data) return null;

  const { recipe, lines } = data;

  return (
    <Card title={recipe.name}>
      <ErrorBanner error={error} />

      <p className="muted small">
        Makes {nf.format(Number(recipe.yield_quantity))}{' '}
        {Number(recipe.yield_quantity) === 1 ? recipe.yield_unit : `${recipe.yield_unit}s`}
        {Number(recipe.yield_quantity) !== 1 && ', so the cost below is per one of them'}.
      </p>

      <div className="table">
        <table className="data">
          <thead>
            <tr>
              <th>Ingredient</th>
              <th className="num">On the plate</th>
              <th className="num">Off the shelf</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td className="cell--title">
                  {l.ingredient}
                  {l.preparation && <span className="muted small"> · {l.preparation}</span>}
                  {!l.is_costed && <span className="muted small"> · not costed</span>}
                </td>
                <td className="num">
                  {nf.format(Number(l.quantity))} {l.unit_code}
                </td>
                <td className="num muted">
                  {/* The trim. Shown because it is the difference between
                      what a recipe asks for and what the walk-in loses,
                      and nothing else on any screen says it. */}
                  {Number(l.yield_percent) === 100 ? (
                    <span>same</span>
                  ) : (
                    <>
                      {nf.format(Number(l.stock_quantity))} {l.stock_unit_code}
                      <span className="small"> · {nf.format(Number(l.yield_percent))}% yield</span>
                    </>
                  )}
                </td>
                <td className="num">
                  {!l.is_costed ? (
                    <span className="muted">—</span>
                  ) : !l.has_cost ? (
                    <Status tone="warning">never bought</Status>
                  ) : (
                    money(Math.round(Number(l.cost_minor)), currency)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className="cell--title">Per portion</td>
              <td className="num">{money(Math.round(Number(recipe.cost_minor)), currency)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {recipe.method && (
        <>
          <h3 className="small">Method</h3>
          <p className="muted">{recipe.method}</p>
        </>
      )}
    </Card>
  );
}

// =====================================================================
// One run
// =====================================================================

function RunDetail({
  runId,
  mayPost,
  onChanged,
}: {
  runId: string;
  mayPost: boolean;
  onChanged: () => void;
}) {
  const { currency } = useSession();
  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);

  const { data, error, loading, reload } = useApi<{
    run: Run;
    departments: any[];
    variance: any[];
  }>(`/v1/recipes/depletions/${runId}`, [runId]);

  const post = useMutation(async () => {
    const r = await api(`/v1/recipes/depletions/${runId}/post`, { method: 'POST' });
    reload();
    onChanged();
    return r;
  });

  const reverse = useMutation(async () => {
    const r = await api(`/v1/recipes/depletions/${runId}/reverse`, {
      method: 'POST',
      body: { reason },
    });
    setReversing(false);
    setReason('');
    reload();
    onChanged();
    return r;
  });

  if (loading && !data) return <Loading rows={2} />;
  if (!data) return null;

  const { run, departments, variance } = data;
  const moved = variance.filter(
    (v) => Number(v.count_variance_minor) !== 0 || Number(v.waste_minor) !== 0,
  );

  return (
    <Card title={`${fmtDate(run.covers_from)} – ${fmtDate(run.covers_to)}`}>
      <ErrorBanner error={error ?? post.error ?? reverse.error} />

      {run.understated && (
        <div className="notice notice--warn">
          <strong>
            {run.items_uncosted} item{run.items_uncosted === 1 ? '' : 's'} sold in this span with
            no recipe, on {run.lines_uncosted} line{run.lines_uncosted === 1 ? '' : 's'}.
          </strong>
          <p className="muted small">
            Their cost is not in the figure below and it is not zero — it is unknown. Write the
            recipes and cost the span again; the same sentence is written into the journal
            entry&rsquo;s own memo for whoever reads it later without this screen.
          </p>
        </div>
      )}

      {run.status === 'draft' && (
        <p className="muted small">
          A draft on the ledger, but not on the shelf: the food has already come off, because it
          already went out. If the figure is wrong the fix is a recipe or a delivery, and the way
          back is a reversal rather than throwing this away.
        </p>
      )}
      {run.status === 'reversed' && (
        <p className="muted small">Reversed: {run.reversed_reason}</p>
      )}

      <div className="tiles">
        <Tile label="Cost of sales" value={money(run.total_minor, currency)} />
        <Tile label="Checks" value={String(run.checks_counted)} />
        <Tile label="Ingredients moved" value={String(run.lines_costed)} />
        <Tile label="Days" value={String(run.days)} />
      </div>

      <div className="table">
        <table className="data data--cards">
          <thead>
            <tr>
              <th>Department</th>
              <th>Shelf</th>
              <th>Account</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {departments.map((d) => (
              <tr key={`${d.department_code}-${d.account_number}-${d.category}`}>
                <td data-label="Department" className="cell--title">{d.department}</td>
                <td data-label="Shelf" className="muted">{d.category}</td>
                <td data-label="Account" className="muted">{d.account_number}</td>
                <td data-label="Cost" className="num">{money(d.amount_minor, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The reason the module exists, loaded with the run. */}
      <h3 className="small">What the recipes say against what the shelf says</h3>
      {moved.length === 0 ? (
        <p className="muted small">
          Nothing counted or thrown away in this span, so there is nothing to compare the recipes
          against yet. Count a shelf and the difference appears here.
        </p>
      ) : (
        <div className="table">
          <table className="data">
            <thead>
              <tr>
                <th>Ingredient</th>
                <th className="num">Recipes say</th>
                <th className="num">Shelf says</th>
                <th className="num">Waste</th>
                <th className="num">Unaccounted</th>
              </tr>
            </thead>
            <tbody>
              {moved.map((v) => (
                <tr key={v.ingredient_id}>
                  <td className="cell--title">{v.ingredient}</td>
                  <td className="num muted">
                    {nf.format(Number(v.theoretical_qty))} {v.stock_unit_code}
                  </td>
                  <td className="num">
                    {nf.format(Number(v.actual_qty))} {v.stock_unit_code}
                  </td>
                  <td className="num">
                    {Number(v.waste_minor) === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      money(v.waste_minor, currency)
                    )}
                  </td>
                  <td className="num">
                    {Number(v.count_variance_minor) === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      <Status tone="warning">{money(v.count_variance_minor, currency)}</Status>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mayPost && (
        <div className="actions">
          {run.status === 'draft' && (
            <button disabled={post.busy} onClick={() => post.run()}>
              {post.busy ? 'Posting…' : `Post ${money(run.total_minor, currency)} to the P&L`}
            </button>
          )}

          {run.status === 'posted' &&
            (!reversing ? (
              <button className="secondary" onClick={() => setReversing(true)}>
                Reverse it
              </button>
            ) : (
              <>
                <label className="field">
                  <span>Why</span>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="The Saturday covers were keyed twice"
                  />
                  <div className="hint">
                    Required. Reversing puts the food back on the shelf as well as the money back
                    on the ledger.
                  </div>
                </label>
                <button disabled={!reason.trim() || reverse.busy} onClick={() => reverse.run()}>
                  {reverse.busy ? 'Reversing…' : 'Reverse it'}
                </button>
                <button className="secondary" onClick={() => setReversing(false)}>
                  Cancel
                </button>
              </>
            ))}
        </div>
      )}
    </Card>
  );
}
